import { useEffect, useState, useCallback } from "react";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminShell from "@/components/AdminShell";

export default function AdminModeration() {
  const { user } = useAuth();
  const [queue, setQueue] = useState({ threads: [], replies: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/moderation/queue").then((r) => { setQueue(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { if (user && user.role === "admin") load(); }, [user, load]);

  const act = async (kind, id, action) => {
    await api.post(`/moderation/${kind}/${id}/${action}`);
    load();
  };

  const total = queue.threads.length + queue.replies.length;

  return (
    <AdminShell active="moderation" title="Moderation" subtitle={`AI-assisted, human-approved. ${total} item${total === 1 ? "" : "s"} awaiting review.`}>
      {loading ? <p className="mt-2 text-mutedplum">Loading…</p> : total === 0 ? (
        <div className="mt-2 bg-white rounded-3xl border border-rose/30 p-12 text-center text-mutedplum" data-testid="mod-empty">The queue is clear. Lovely work. ✨</div>
      ) : (
        <div className="mt-2 space-y-8">
          {queue.threads.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-plum mb-3">Discussions ({queue.threads.length})</h2>
              <div className="space-y-3">
                {queue.threads.map((t) => (
                  <div key={t.thread_id} className="bg-white rounded-2xl border border-rose/30 p-5" data-testid={`mod-thread-${t.thread_id}`}>
                    <p className="text-xs text-mutedplum">{t.category_slug} · by {t.author_name}</p>
                    <h3 className="font-display text-lg text-plum mt-1">{t.title}</h3>
                    <p className="mt-1 text-[15px] text-plum-ink/80 line-clamp-3">{t.body}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => act("thread", t.thread_id, "approve")} data-testid={`approve-thread-${t.thread_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-sm font-semibold text-white hover:bg-teal-dark transition-colors"><Check className="h-4 w-4" /> Approve</button>
                      <button onClick={() => act("thread", t.thread_id, "reject")} data-testid={`reject-thread-${t.thread_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border-2 border-rose/60 px-4 py-2 text-sm font-semibold text-plum hover:bg-rose-soft transition-colors"><X className="h-4 w-4" /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {queue.replies.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-plum mb-3">Replies ({queue.replies.length})</h2>
              <div className="space-y-3">
                {queue.replies.map((r) => (
                  <div key={r.reply_id} className="bg-white rounded-2xl border border-rose/30 p-5" data-testid={`mod-reply-${r.reply_id}`}>
                    <p className="text-xs text-mutedplum">by {r.author_name}</p>
                    <p className="mt-1 text-[15px] text-plum-ink/80">{r.body}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => act("reply", r.reply_id, "approve")} data-testid={`approve-reply-${r.reply_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-sm font-semibold text-white hover:bg-teal-dark transition-colors"><Check className="h-4 w-4" /> Approve</button>
                      <button onClick={() => act("reply", r.reply_id, "reject")} data-testid={`reject-reply-${r.reply_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border-2 border-rose/60 px-4 py-2 text-sm font-semibold text-plum hover:bg-rose-soft transition-colors"><X className="h-4 w-4" /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </AdminShell>
  );
}
